import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Tag,
  Space,
  Descriptions,
  Row,
  Col,
  Typography,
  Divider,
  Badge,
  Empty,
  Button,
  Tabs,
  Image,
  Tooltip,
  Form,
  Input,
} from 'antd';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Icons from '@/components/third-party/Icons';
import { PageRail } from '@/components/PageChrome';
import PageHeader from '@/components/PageHeader';
import PageModal from '@/components/PageModal';
import StatCard from '@/components/StatCard';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { liveStatusRailItem } from '@/pages/dashboard/equipmentRoomsLink';
import DataTable from '@/components/DataTable';
import GmpNumber from '@/components/GmpNumber';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import { formatDateTime } from '@/utils/datetime';
import { useMbmrAlias } from '@/hooks/useAppSetting';
import {
  get_completed_batches_for_cleaning,
  get_equipment_list_for_cleaning,
  get_cleaning_logs_by_batch,
  get_cleaning_logs_by_equipment,
  start_machine_cleaning,
  complete_machine_cleaning,
  approve_machine_cleaning,
} from '@/utils/api';
import { exportCleaningLogsPdf } from '@/pages/Batch-Processing/cleaningLogsExport';

const { Text } = Typography;
const { Option } = Select;

const hasValue = (v) => v !== null && v !== undefined && v !== '' && v !== 'N/A';

const CleaningLogs = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const mbmrAlias = useMbmrAlias();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Dark mode colors
  const pageBgColor = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const nestedBgColor = isDark ? defaultColors.palette.gray[700] : '#f9fafb';
  const highlightBgColor = cardBgColor;
  const highlightBorderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const descStyles = {
    label: { backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f3f4f6', color: secondaryTextColor, fontWeight: 600, fontSize: 13, width: 150 },
    content: { backgroundColor: cardBgColor, color: textColor, fontSize: 13 },
  };
  const verifiedBgColor = isDark ? '#14532d' : '#f0fdf4';
  const verifiedBorderColor = isDark ? '#166534' : '#bbf7d0';
  const approvedBgColor = isDark ? defaultColors.palette.teal[900] : defaultColors.palette.teal[50];
  const approvedBorderColor = isDark ? defaultColors.palette.teal[700] : defaultColors.palette.teal[200];

  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('batch'); // 'batch' or 'equipment'
  const [batchList, setBatchList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [logData, setLogData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [completeForm] = Form.useForm();
  const [approveForm] = Form.useForm();
  const [exporting, setExporting] = useState(false);

  // Mock batch list
  const mockBatchList = [
    { batch_number: 'BT-2025-0001', recipe_name: 'Aspirin Tablet 500mg', status: 'In Progress' },
    { batch_number: 'BT-2025-0002', recipe_name: 'Paracetamol Syrup', status: 'Completed' },
    { batch_number: 'BT-2025-0003', recipe_name: 'Ibuprofen Tablet 400mg', status: 'Completed' },
  ];

  useEffect(() => {
    fetchBatchList();
    fetchEquipmentList();
  }, []);

  // ?equipment=<bp_machine id> (from Equipment & Room Status) opens the page on that machine's cleaning log.
  useEffect(() => {
    const id = searchParams.get('equipment');
    if (!id) return;
    setFilterType('equipment');
    handleEquipmentChange(Number.isNaN(Number(id)) ? id : Number(id));
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBatchList = async () => {
    setLoading(true);
    try {
      get_completed_batches_for_cleaning((response) => {
        if (response?.data?.status === 'success') {
          setBatchList(response.data.data || []);
        } else {
          console.error('Error fetching batch list:', response?.data?.message);
          toast.error(response?.data?.message || 'Failed to fetch batch list');
          setBatchList([]);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching batch list:', error);
      toast.error('Failed to fetch batch list');
      setBatchList([]);
      setLoading(false);
    }
  };

  const fetchEquipmentList = async () => {
    setLoading(true);
    try {
      get_equipment_list_for_cleaning((response) => {
        if (response?.data?.status === 'success') {
          setEquipmentList(response.data.data || []);
        } else {
          console.error('Error fetching equipment list:', response?.data?.message);
          toast.error(response?.data?.message || 'Failed to fetch equipment list');
          setEquipmentList([]);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching equipment list:', error);
      toast.error('Failed to fetch equipment list');
      setEquipmentList([]);
      setLoading(false);
    }
  };

  const fetchLogData = async (type, value) => {
    setLoading(true);
    try {
      if (type === 'batch') {
        get_cleaning_logs_by_batch(value, (response) => {
          if (response?.data?.status === 'success') {
            // Backend returns batch_info and cleaning_activities directly
            const data = {
              batch_number: response.data.batch_info?.batch_number,
              recipe_name: response.data.batch_info?.recipe_name,
              product_code: response.data.batch_info?.product_code,
              cleaning_activities: response.data.cleaning_activities || [],
            };
            setLogData(data);
          } else {
            console.error('Error fetching log data:', response?.data?.message);
            toast.error(response?.data?.message || 'Failed to fetch cleaning logs');
            setLogData(null);
          }
          setLoading(false);
        });
      } else if (type === 'equipment') {
        get_cleaning_logs_by_equipment(value, (response) => {
          if (response?.data?.status === 'success') {
            // Backend returns equipment_info and cleaning_activities directly
            const data = {
              equipment_id: response.data.equipment_info?.equipment_id,
              equipment_name: response.data.equipment_info?.equipment_name,
              equipment_code: response.data.equipment_info?.equipment_code,
              equipment_type: response.data.equipment_info?.equipment_type,
              cleaning_activities: response.data.cleaning_activities || [],
            };
            setLogData(data);
          } else {
            console.error('Error fetching log data:', response?.data?.message);
            toast.error(response?.data?.message || 'Failed to fetch cleaning logs');
            setLogData(null);
          }
          setLoading(false);
        });
      }
    } catch (error) {
      console.error('Error fetching log data:', error);
      setLoading(false);
      toast.error('Failed to fetch cleaning logs');
    }
  };

  const handleBatchChange = (batchNumber) => {
    setSelectedBatch(batchNumber);
    setSelectedEquipment(null);
    if (batchNumber) {
      fetchLogData('batch', batchNumber);
    } else {
      setLogData(null);
    }
  };

  const handleEquipmentChange = (equipmentId) => {
    setSelectedEquipment(equipmentId);
    setSelectedBatch(null);
    if (equipmentId) {
      fetchLogData('equipment', equipmentId);
    } else {
      setLogData(null);
    }
  };

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    setSelectedBatch(null);
    setSelectedEquipment(null);
    setLogData(null);
  };

  // Action Handlers
  const handleStartCleaning = (record) => {
    setActionLoading(true);
    const data = {
      remarks: 'Starting cleaning process',
    };
    start_machine_cleaning(record.id, data, (response) => {
      if (response?.status === 'success') {
        toast.success('Cleaning started successfully');
        // Refresh the log data
        if (selectedBatch) {
          fetchLogData('batch', selectedBatch);
        } else if (selectedEquipment) {
          fetchLogData('equipment', selectedEquipment);
        }
      } else {
        toast.error(response?.message || 'Failed to start cleaning');
      }
      setActionLoading(false);
    });
  };

  const handleCompleteCleaningClick = (record) => {
    setSelectedRecord(record);
    setCompleteModalVisible(true);
    completeForm.resetFields();
  };

  const handleCompleteCleaningSubmit = () => {
    completeForm
      .validateFields()
      .then((values) => {
        setActionLoading(true);
        const data = {
          verification_method: values.verification_method || 'VISUAL',
          verification_results: values.verification_results,
          cleaning_agents_used: values.cleaning_agents_used,
          remarks: values.remarks,
        };
        complete_machine_cleaning(selectedRecord.id, data, (response) => {
          if (response?.status === 'success') {
            toast.success('Cleaning completed and sent for approval');
            setCompleteModalVisible(false);
            // Refresh the log data
            if (selectedBatch) {
              fetchLogData('batch', selectedBatch);
            } else if (selectedEquipment) {
              fetchLogData('equipment', selectedEquipment);
            }
          } else {
            toast.error(response?.message || 'Failed to complete cleaning');
          }
          setActionLoading(false);
        });
      })
      .catch((errorInfo) => {
        // Form validation failed
        console.log('Validation Failed:', errorInfo);
        toast.error('Please fill in all required fields');
      });
  };

  const handleApproveRejectClick = (record, action) => {
    setSelectedRecord({ ...record, action });
    setApproveModalVisible(true);
    approveForm.resetFields();
  };

  const handleApproveRejectSubmit = () => {
    approveForm
      .validateFields()
      .then((values) => {
        setActionLoading(true);
        const data = {
          approval_status: selectedRecord.action === 'approve' ? 'APPROVED' : 'REJECTED',
          comments: values.comments,
        };
        approve_machine_cleaning(selectedRecord.id, data, (response) => {
          if (response?.status === 'success') {
            toast.success(
              selectedRecord.action === 'approve'
                ? 'Cleaning approved. Machine is now available.'
                : 'Cleaning rejected. Please re-clean.'
            );
            setApproveModalVisible(false);
            // Refresh the log data
            if (selectedBatch) {
              fetchLogData('batch', selectedBatch);
            } else if (selectedEquipment) {
              fetchLogData('equipment', selectedEquipment);
            }
          } else {
            toast.error(response?.message || 'Failed to process approval');
          }
          setActionLoading(false);
        });
      })
      .catch((errorInfo) => {
        // Form validation failed
        console.log('Validation Failed:', errorInfo);
        toast.error('Please fill in all required fields');
      });
  };

  const getStatusColor = (status) => {
    const colors = {
      COMPLETED: 'success',
      APPROVED: 'success',
      IN_PROGRESS: 'processing',
      PENDING: 'warning',
      REJECTED: 'error',
      Completed: 'success',
      'In Progress': 'processing',
      Pending: 'warning',
      Failed: 'error',
      Scheduled: 'default',
    };
    return colors[status] || 'default';
  };

  const acts = logData?.cleaning_activities || [];

  const handleExport = () => {
    if (!logData) {
      toast.warning('Select a batch or equipment and wait for logs to load before exporting');
      return;
    }
    setExporting(true);
    try {
      const ok = exportCleaningLogsPdf(logData, { mbmrAlias });
      if (ok) {
        toast.success('Opening A4 PDF report — use Save as PDF in the print dialog');
      } else {
        toast.error('Failed to generate Cleaning Logs PDF');
      }
    } catch (error) {
      console.error('Error exporting cleaning logs PDF:', error);
      toast.error('Failed to export Cleaning Logs PDF');
    } finally {
      setExporting(false);
    }
  };

  // Cleaning Activity Columns
  const activityColumns = [
    {
      title: 'Record No.',
      dataIndex: 'id',
      key: 'gmp_number',
      width: 240,
      align: 'left',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (id) => <GmpNumber objectType="MachineCleaning" objectRef={id} kind="CLEANING" showEmpty style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', margin: 0 }} />,
    },
    {
      title: 'Equipment Name',
      dataIndex: 'equipment_name',
      key: 'equipment_name',
      width: 180,
      render: (text, record) => (
        <div>
          <Text strong style={{ color: defaultColors.primary }}>
            {text}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.equipment_code || record.equipment_id || ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Equipment Type',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      width: 170,
      render: (type) => (
        <Tooltip title={type}>
          <Tag>
            {type?.length > 20 ? `${type?.slice(0, 20)}...` : type ? type : 'N/A'}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Area',
      dataIndex: 'area',
      key: 'area',
      width: 150,
    },
    {
      title: 'Cleaning Type',
      dataIndex: 'cleaning_type',
      key: 'cleaning_type',
      width: 230,
      render: (type) => {
        const typeName = type?.name || type || 'Standard';
        const category = type?.category || '';
        return (
          <Tooltip title={typeName}>
            <Tag style={{ whiteSpace: 'normal', height: 'auto' }}>{typeName}</Tag>
            {category && (
              <div>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {category}
                </Text>
              </div>
            )}
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (text) => formatDateTime(text, 'DD-MMM-YYYY HH:mm'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 180,
      render: (text) => formatDateTime(text, 'DD-MMM-YYYY HH:mm'),
    },
    {
      title: 'Duration (min)',
      dataIndex: 'duration_minutes',
      key: 'duration_minutes',
      width: 120,
      render: (duration) => (duration ? `${duration} min` : null),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (text, record) => (
        <Space size="small" direction="vertical">
          {record.status === 'PENDING' && (
            <Button
              type="primary"
              size="small"
              icon={<Icons.PlayCircle size={20} />}
              onClick={() => handleStartCleaning(record)}
              loading={actionLoading}
              style={{ width: '100%' }}
            >
              Start
            </Button>
          )}
          {record.status === 'IN_PROGRESS' && (
            <Button
              type="primary"
              size="small"
              icon={<Icons.CheckCircle size={20} />}
              onClick={() => handleCompleteCleaningClick(record)}
              loading={actionLoading}
              style={{ width: '100%' }}
            >
              Complete
            </Button>
          )}
          {record.status === 'COMPLETED' && (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                icon={<Icons.Check size={20} />}
                onClick={() => handleApproveRejectClick(record, 'approve')}
                loading={actionLoading}
                style={{ background: defaultColors.success, borderColor: defaultColors.success }}
              >
                Approve
              </Button>
              <Button
                type="primary"
                danger
                size="small"
                icon={<Icons.X size={20} />}
                onClick={() => handleApproveRejectClick(record, 'reject')}
                loading={actionLoading}
              >
                Reject
              </Button>
            </Space>
          )}
          {(record.status === 'APPROVED' || record.status === 'REJECTED') && (
            <Tag color={record.status === 'APPROVED' ? 'green' : 'red'}>
              {record.status === 'APPROVED' ? 'Approved' : 'Rejected'}
            </Tag>
          )}
        </Space>
      ),
    },
  ];

  // Cleaning Steps Columns
  const stepsColumns = [
    {
      title: 'Step No',
      dataIndex: 'step_number',
      key: 'step_number',
      width: 80,
      render: (num) => <Tag>{num}</Tag>,
    },
    {
      title: 'Step Name',
      dataIndex: 'step_name',
      key: 'step_name',
      width: 200,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Performed By',
      dataIndex: 'performed_by',
      key: 'performed_by',
      width: 150,
      render: (text) => (
        <Space>
          <Icons.User size={16} style={{ color: defaultColors.primary }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Start Time',
      // Backend already sends a display-ready, correctly-zoned string for each
      // step (action_time, e.g. "05-Jun-2026 02:31:56 PM") — use it as-is
      // instead of reformatting the raw start_time, which stays for sorting only.
      dataIndex: 'action_time',
      key: 'action_time',
      width: 180,
      render: (text, record) => text || formatDateTime(record.start_time, 'DD-MMM-YYYY HH:mm'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 180,
      render: (text) => formatDateTime(text, 'DD-MMM-YYYY HH:mm'),
    },
    {
      title: 'Duration (min)',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => (duration ? `${duration} min` : null),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      render: (text) => text || '-',
    },
  ];

  // Render Cleaning Steps Expand
  const renderCleaningSteps = (activity) => {
    if (!activity.cleaning_steps || activity.cleaning_steps.length === 0) {
      return (
        <div className="p-4" style={{ backgroundColor: nestedBgColor }}>
          <Empty description="No cleaning steps recorded" />
        </div>
      );
    }

    return (
      <div className="p-4" style={{ backgroundColor: nestedBgColor }}>
        <div className="flex items-center gap-2 mb-3" style={{ color: textColor }}>
          <Icons.Droplet size={16} style={{ color: defaultColors.primary }} />
          <span style={{ fontSize: 14, fontWeight: 700 }}>Cleaning Steps</span>
        </div>

        {/* Cleaning Details */}
        {(hasValue(activity.previous_product) ||
          hasValue(activity.previous_batch) ||
          hasValue(activity.cleaning_procedure) ||
          hasValue(activity.next_product) ||
          hasValue(activity.next_batch) ||
          hasValue(activity.scheduled_date)) && (
          <Card className="mb-4" size="small" style={{ backgroundColor: highlightBgColor, borderColor: highlightBorderColor }}>
            <Descriptions bordered size="small" column={3} styles={descStyles}>
              {hasValue(activity.previous_product) && (
                <Descriptions.Item label="Previous Product">{activity.previous_product}</Descriptions.Item>
              )}
              {hasValue(activity.previous_batch) && (
                <Descriptions.Item label="Previous Batch">{activity.previous_batch}</Descriptions.Item>
              )}
              {hasValue(activity.cleaning_procedure) && (
                <Descriptions.Item label="Cleaning Procedure">{activity.cleaning_procedure}</Descriptions.Item>
              )}
              {hasValue(activity.next_product) && (
                <Descriptions.Item label="Next Product">{activity.next_product}</Descriptions.Item>
              )}
              {hasValue(activity.next_batch) && (
                <Descriptions.Item label="Next Batch">{activity.next_batch}</Descriptions.Item>
              )}
              {hasValue(activity.scheduled_date) && (
                <Descriptions.Item label="Scheduled Date">
                  {formatDateTime(activity.scheduled_date, 'DD-MMM-YYYY')}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        )}

        {/* Steps Table */}
        <DataTable
          height={null}
          columns={stepsColumns}
          data={activity.cleaning_steps}
          pagination={false}
          size="small"
          rowKey="step_id"
          bordered
          expandable={{
            expandedRowRender: (step) => renderStepDetails(step),
            rowExpandable: (record) => record.cleaning_agent || record.attachments?.length > 0,
          }}
        />

        {/* Verification & Approval */}
        {(activity.verified_by || activity.approved_by) && (
          <>
            <Divider />
            <Row gutter={16}>
              {activity.verified_by && (
                <Col span={activity.verified_by && activity.approved_by ? 12 : 24}>
                  <Card size="small" style={{ backgroundColor: verifiedBgColor, borderColor: verifiedBorderColor }}>
                    <div className="flex flex-wrap items-center" style={{ columnGap: 24, rowGap: 4 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icons.CheckCircle size={16} style={{ color: defaultColors.success }} />
                        <Text strong style={{ color: textColor }}>Verified By</Text>
                      </span>
                      <Text style={{ color: textColor }}>{activity.verified_by}</Text>
                      <Text style={{ fontSize: '12px', color: secondaryTextColor }}>
                        {formatDateTime(activity.verified_at, 'DD-MMM-YYYY HH:mm')}
                      </Text>
                    </div>
                  </Card>
                </Col>
              )}
              {activity.approved_by && (
                <Col span={activity.verified_by && activity.approved_by ? 12 : 24}>
                  <Card size="small" style={{ backgroundColor: approvedBgColor, borderColor: approvedBorderColor }}>
                    <div className="flex flex-wrap items-center" style={{ columnGap: 24, rowGap: 4 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icons.CheckCircle size={16} style={{ color: defaultColors.primary }} />
                        <Text strong style={{ color: textColor }}>Approved By</Text>
                      </span>
                      <Text style={{ color: textColor }}>{activity.approved_by}</Text>
                      <Text style={{ fontSize: '12px', color: secondaryTextColor }}>
                        {formatDateTime(activity.approved_at, 'DD-MMM-YYYY HH:mm')}
                      </Text>
                    </div>
                  </Card>
                </Col>
              )}
            </Row>
          </>
        )}
      </div>
    );
  };

  // Render Step Details (Cleaning Agent, Water, Attachments)
  const renderStepDetails = (step) => {
    return (
      <div className="p-4" style={{ backgroundColor: cardBgColor }}>
        <Row gutter={16}>
          {/* Cleaning Agent Details */}
          {step.cleaning_agent && (
            <Col span={12}>
              <Descriptions title="Cleaning Agent Details" bordered size="small" column={1}>
                <Descriptions.Item label="Cleaning Agent">{step.cleaning_agent}</Descriptions.Item>
                {step.concentration && (
                  <Descriptions.Item label="Concentration">{step.concentration}</Descriptions.Item>
                )}
                {step.temperature && (
                  <Descriptions.Item label="Temperature">
                    {step.temperature} {step.temperature_uom}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          )}

          {/* Water Details */}
          {step.water_type && (
            <Col span={12}>
              <Descriptions title="Rinse Water Details" bordered size="small" column={1}>
                <Descriptions.Item label="Water Type">{step.water_type}</Descriptions.Item>
                {step.water_volume && (
                  <Descriptions.Item label="Volume">
                    {step.water_volume} {step.water_volume_uom}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          )}

          {/* Inspection Results */}
          {step.inspection_result && (
            <Col span={12}>
              <Descriptions title="Inspection Results" bordered size="small" column={1}>
                <Descriptions.Item label="Visual Inspection">
                  <Tag color={step.inspection_result === 'Pass' ? 'green' : 'red'}>
                    {step.inspection_result}
                  </Tag>
                </Descriptions.Item>
                {step.swab_test_result && (
                  <Descriptions.Item label="Swab Test">
                    <Tag color={step.swab_test_result === 'Negative' ? 'green' : 'red'}>
                      {step.swab_test_result}
                    </Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          )}

          {/* Attachments */}
          {step.attachments && step.attachments.length > 0 && (
            <Col span={24}>
              <Divider orientation="left">Attachments</Divider>
              <Space wrap>
                {step.attachments.map((attachment) => (
                  <Card
                    key={attachment.attachment_id}
                    size="small"
                    style={{ backgroundColor: cardBgColor }}
                  >
                    <Space direction="vertical" size="small">
                      {attachment.file_type === 'image' ? (
                        <Image
                          width={100}
                          src={attachment.file_path}
                          alt={attachment.file_name}
                          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                        />
                      ) : (
                        <Icons.FileText size={48} style={{ color: defaultColors.primary }} />
                      )}
                      <Text style={{ fontSize: '12px' }}>{attachment.file_name}</Text>
                      <Button
                        type="link"
                        size="small"
                        icon={<Icons.Download size={14} />}
                        onClick={() =>
                          toast.info('Download functionality - API integration needed')
                        }
                      >
                        Download
                      </Button>
                    </Space>
                  </Card>
                ))}
              </Space>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  return (
    <div className="p-2.5 pt-5" style={{ backgroundColor: pageBgColor }}>
      <Card
        className="border border-gray-300 shadow-md mt-2"
        bordered={false}
        classNames={{ body: 'page-card-scroll' }}
        style={{ backgroundColor: cardBgColor, height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<Icons.Droplet size={22} />}
            title={t('Digital Cleaning Logs')}
            subtitle={selectedBatch || selectedEquipment || undefined}
            description="Cleaning records by batch or by equipment: each step, who did it, and the verification and approval."
            view={{
              value: filterType,
              onChange: handleFilterTypeChange,
              options: [
                { label: 'Batch Number', value: 'batch' },
                { label: 'Equipment ID', value: 'equipment' },
              ],
            }}
            filters={
              filterType === 'batch' ? (
                <Select
                  aria-label="Select batch number"
                  placeholder="Select a batch to view cleaning logs"
                  value={selectedBatch}
                  onChange={handleBatchChange}
                  style={{ width: 380 }}
                  showSearch
                  allowClear
                  optionLabelProp="label"
                  filterOption={(input, option) => String(option.searchText || '').toLowerCase().includes(input.toLowerCase())}
                  loading={loading}
                >
                  {batchList.map((batch) => (
                    <Option key={batch.batch_number} value={batch.batch_number} label={batch.batch_number} searchText={`${batch.batch_number} ${batch.recipe_name || ''}`}>
                      <Space>
                        <Text strong>{batch.batch_number}</Text>
                        <Text type="secondary">-</Text>
                        <Text>{batch.recipe_name}</Text>
                      </Space>
                    </Option>
                  ))}
                </Select>
              ) : (
                <Select
                  aria-label="Select equipment"
                  placeholder="Select an equipment to view cleaning logs"
                  value={selectedEquipment}
                  onChange={handleEquipmentChange}
                  style={{ width: 380 }}
                  showSearch
                  allowClear
                  optionLabelProp="label"
                  filterOption={(input, option) => String(option.searchText || '').toLowerCase().includes(input.toLowerCase())}
                  loading={loading}
                >
                  {equipmentList.map((equipment) => (
                    <Option key={equipment.equipment_id} value={equipment.equipment_id} label={`${equipment.equipment_id} - ${equipment.equipment_name}`} searchText={`${equipment.equipment_id} ${equipment.equipment_name || ''} ${equipment.equipment_type || ''}`}>
                      <Space>
                        <Text strong>{equipment.equipment_id}</Text>
                        <Text type="secondary">-</Text>
                        <Text>{equipment.equipment_name}</Text>
                        <Tag style={{ marginLeft: 8 }}>{equipment.equipment_type}</Tag>
                      </Space>
                    </Option>
                  ))}
                </Select>
              )
            }
          />
        }
      >
        <PageRail
          items={[
            {
              key: 'export',
              icon: Icons.Download,
              label: 'Export PDF',
              onClick: handleExport,
              loading: exporting,
              disabled: !logData,
            },
            // Live status → Equipment & Room Status, pre-filtered when an equipment is selected.
            liveStatusRailItem(navigate, { equipment: selectedEquipment }),
          ]}
        />
        {/* Filter Header Information */}
        {logData && (
          <>
            {/* what is being viewed, as a plain line — no box */}
            <div className="mb-3" style={{ fontSize: 13, color: secondaryTextColor, display: 'flex', flexWrap: 'wrap', gap: '2px 20px' }}>
              {(logData.batch_number
                ? [['Batch', logData.batch_number], [`${mbmrAlias}`, logData.recipe_name], ['Product code', logData.product_code]]
                : [['Equipment ID', logData.equipment_id], ['Equipment', logData.equipment_name]]
              ).filter(([, v]) => hasValue(v)).map(([k, v]) => (
                <span key={k}>{k}: <b style={{ color: textColor }}>{v}</b></span>
              ))}
            </div>

            {/* counts of what is on screen, by where each cleaning stands */}
            <div className="flex gap-3 mb-3" style={{ flexWrap: 'wrap' }}>
              {[
                ['Cleanings', <Icons.Droplet size={18} />, acts.length, defaultColors.primary],
                ['Pending', <Icons.Clock size={18} />, acts.filter((r) => r.status === 'PENDING').length, defaultColors.palette.gray[500]],
                ['In progress', <Icons.PlayCircle size={18} />, acts.filter((r) => r.status === 'IN_PROGRESS').length, defaultColors.warning],
                ['Awaiting approval', <Icons.CheckCircle size={18} />, acts.filter((r) => r.status === 'COMPLETED').length, defaultColors.palette.teal[400]],
                ['Approved', <Icons.Check size={18} />, acts.filter((r) => r.status === 'APPROVED').length, defaultColors.success],
                ['Rejected', <Icons.X size={18} />, acts.filter((r) => r.status === 'REJECTED').length, defaultColors.error],
              ].map(([label, icon, value, tone]) => (
                <StatCard key={label} icon={icon} label={label} value={value} bgColor={`${tone}22`} iconColor={tone} />
              ))}
            </div>

            <DataTable
              tableKey="cleaningLogsActivities"
              title="Cleaning Activities"
              searchable
              searchPlaceholder="Search cleaning activities…"
              searchKeys={['equipment_name', 'equipment_code', 'area', 'status']}
              columnConfig
              showCount={false}
              emptyMessage="No cleaning activities recorded"
              pagination={{ pageSize: 15 }}
              columns={activityColumns}
              data={logData.cleaning_activities}
              loading={loading}
              rowKey="id"
              bordered
              // No max-height cap — original had no scroll.y either, table just grows
              // with content — but a floor keeps it from looking collapsed with 1-2 rows.
              height={null}
              minHeight={240}
              scrollX={1600}
              expandable={{
                expandedRowRender: renderCleaningSteps,
                rowExpandable: (record) => true,
              }}
            />
          </>
        )}

        {/* No Selection */}
        {!selectedBatch && !selectedEquipment && (
          <div className="text-center py-12">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  <Text className="text-lg" style={{ color: secondaryTextColor }}>
                    Please select a {filterType === 'batch' ? 'batch number' : 'equipment'} to view
                    cleaning logs
                  </Text>
                </span>
              }
            />
          </div>
        )}
      </Card>

      {/* Complete Cleaning Modal */}
      <PageModal
        icon={<Icons.CheckCircle size={22} />}
        title="Complete Cleaning"
        description="Record how the cleaning was verified. It then goes for approval."
        open={completeModalVisible}
        onCancel={() => setCompleteModalVisible(false)}
        width={600}
        footer={(
          <div className="flex justify-end gap-2">
            <Button onClick={() => setCompleteModalVisible(false)}>Cancel</Button>
            <Button type="primary" loading={actionLoading} onClick={handleCompleteCleaningSubmit}>OK</Button>
          </div>
        )}
      >
        <Form form={completeForm} layout="vertical">
          <Form.Item
            label="Verification Method"
            name="verification_method"
            initialValue="VISUAL"
            rules={[{ required: true, message: 'Please select verification method' }]}
          >
            <Select>
              <Option value="VISUAL">Visual Inspection</Option>
              <Option value="SWAB_TEST">Swab Test</Option>
              <Option value="RINSE_SAMPLE">Rinse Sample</Option>
              <Option value="ATP_TEST">ATP Test</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Verification Results"
            name="verification_results"
            rules={[{ required: true, message: 'Please enter verification results' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter verification results (e.g., 'Passed', 'All surfaces clean', etc.)"
            />
          </Form.Item>

          <Form.Item
            label="Cleaning Agents Used"
            name="cleaning_agents_used"
            rules={[{ required: true, message: 'Please enter cleaning agents used' }]}
          >
            <Input.TextArea
              rows={2}
              placeholder="Enter cleaning agents used (e.g., 'Detergent A, Sanitizer B')"
            />
          </Form.Item>

          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} placeholder="Additional remarks (optional)" />
          </Form.Item>
        </Form>
      </PageModal>

      {/* Approve/Reject Cleaning Modal */}
      <PageModal
        icon={selectedRecord?.action === 'approve' ? <Icons.Check size={22} /> : <Icons.X size={22} />}
        title={selectedRecord?.action === 'approve' ? 'Approve Cleaning' : 'Reject Cleaning'}
        description={selectedRecord?.action === 'approve' ? 'Approving makes the machine available again.' : 'Rejecting sends the machine back to be re-cleaned.'}
        open={approveModalVisible}
        onCancel={() => setApproveModalVisible(false)}
        width={500}
        footer={(
          <div className="flex justify-end gap-2">
            <Button onClick={() => setApproveModalVisible(false)}>Cancel</Button>
            <Button
              type="primary"
              danger={selectedRecord?.action === 'reject'}
              loading={actionLoading}
              onClick={handleApproveRejectSubmit}
              style={selectedRecord?.action === 'approve' ? { background: defaultColors.success, borderColor: defaultColors.success } : {}}
            >
              {selectedRecord?.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </div>
        )}
      >
        <Form form={approveForm} layout="vertical">
          <Form.Item
            label="Comments"
            name="comments"
            rules={[{ required: true, message: 'Please enter comments' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder={
                selectedRecord?.action === 'approve'
                  ? 'Enter approval comments (e.g., "Cleaning verified and approved")'
                  : 'Enter rejection reason (e.g., "Cleaning not satisfactory, please re-clean")'
              }
            />
          </Form.Item>
        </Form>
      </PageModal>
    </div>
  );
};

export default CleaningLogs;
